import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowRight } from 'lucide-react'
import type { ClientProfileCompletionResult } from '@/lib/hooks/use-client-profile-completion'

interface ClientNextStepsProps {
  completion: ClientProfileCompletionResult
}

/**
 * Component showing profile completion status and next actions on client dashboard
 */
export function ClientNextSteps({ completion }: ClientNextStepsProps) {
  const navigate = useNavigate()

  if (completion.isComplete) {
    return null
  }

  return (
    <div
      className="rounded-[40px] border-[3px] border-[rgba(245,158,11,0.60)] p-8 mb-8"
      style={{
        background: 'linear-gradient(99.87deg, rgba(245, 158, 11, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)',
      }}
    >
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Left: Progress Info */}
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">Complete Your Profile</h3>
              <p className="text-sm text-slate-500">Get matched with AI experts faster</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 bg-white rounded-full h-3 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-500"
                style={{ width: `${completion.percentage}%` }}
              />
            </div>
            <span className="text-2xl font-bold text-slate-900">{completion.percentage}%</span>
          </div>

          <p className="text-slate-600">
            Complete your profile to help AI experts understand your needs and increase your chances of finding the perfect match.
          </p>
        </div>

        {/* Right: CTA Button */}
        <div className="lg:w-[320px] flex items-center justify-center">
          <button
            onClick={() => navigate('/client/profile')}
            className="flex items-center gap-2 px-8 py-4 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold text-base transition-colors shadow-lg"
          >
            Complete Profile
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
}
